<template>
    <modal
        :adaptive="true"
        :class="{ 'mobile-item-modal': pivotY === 1 }"
        :clickToClose="true"
        :maxWidth="600"
        :pivotY="pivotY"
        :reset="true"
        :scrollable="true"
        height="800"
        name="allergen-filters-modal"
        transition="pop-out"
        width="600"
    >
        <div
            class="item-modal-content relative flex flex-col w-full h-full bg-page"
        >
            <div class="flex-1 overflow-y-auto p-6 pt-8">
                <div class="flex items-center justify-between mb-4">
                    <h4 class="text-primary text-xl font-bold"> Filters </h4>
                    <button
                        aria-label="Hide basket"
                        class="text-primary transition duration-150 ease-in-out hover:text-gray-500 focus:text-gray-500 focus:outline-none"
                        type="button"
                        @click="$modal.hide('allergen-filters-modal')"
                    >
                        <close-icon />
                    </button>
                </div>

                <div class="mb-6">
                    <h5 class="text-primary text-lg font-semibold mb-2">
                        {{ $t('dietFilters.title') }}
                    </h5>
                    <p class="text-gray-600 text-sm mb-4 leading-relaxed">
                        {{ $t('dietFilters.description') }}
                    </p>

                    <div class="grid grid-cols-2 gap-3">
                        <label
                            v-for="diet in availableDietFilters"
                            :key="diet.key"
                            :for="`diet-${diet.key}`"
                            :class="{
                                'border-button bg-gray-50':
                                    selectedDietFilters.includes(diet.key)
                            }"
                            class="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-button transition-colors cursor-pointer"
                        >
                            <input
                                :id="`diet-${diet.key}`"
                                v-model="selectedDietFilters"
                                :value="diet.key"
                                class="optin-checkbox form-checkbox h-4 w-4 cursor-pointer rounded border-gray-200 transition duration-150 ease-in-out focus:border-blue-200 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                type="checkbox"
                            />
                            <span
                                class="text-sm font-medium text-gray-700 flex-1 select-none"
                            >
                                {{ diet.name }}
                            </span>
                        </label>
                    </div>
                </div>

                <div class="mb-8">
                    <h5 class="text-primary text-lg font-semibold mb-2">
                        {{ $t('allergenFilters.title') }}
                    </h5>
                    <p class="text-gray-600 text-sm mb-4 leading-relaxed">
                        {{ $t('allergenFilters.description') }}
                    </p>

                    <div class="grid grid-cols-2 gap-3">
                        <label
                            v-for="allergen in availableAllergens"
                            :key="allergen.key"
                            :for="`allergen-${allergen.key}`"
                            :class="{
                                'border-button bg-gray-50':
                                    selectedAllergens.includes(allergen.key)
                            }"
                            class="flex items-center space-x-3 p-3 bg-white rounded-lg border hover:border-button transition-colors cursor-pointer"
                        >
                            <input
                                :id="`allergen-${allergen.key}`"
                                v-model="selectedAllergens"
                                :value="allergen.key"
                                class="optin-checkbox form-checkbox h-4 w-4 cursor-pointer rounded border-gray-200 transition duration-150 ease-in-out focus:border-blue-200 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                type="checkbox"
                            />
                            <span
                                class="text-sm font-medium text-gray-700 flex-1 select-none"
                            >
                                {{ allergen.name }}
                            </span>
                        </label>
                    </div>
                </div>

                <div class="mb-6 text-center">
                    <base-button
                        v-if="!showClearedMessage"
                        :button-text="$t('menu.clearAllFilters')"
                        :is-border-transparent="true"
                        class="p-3 px-10"
                        size="sm"
                        variant="secondaryActions"
                        @clicked="clearAllFilters"
                    />
                    <div v-else class="text-gray-600 text-sm font-medium py-3">
                        {{ $t('menu.filtersCleared') }}
                    </div>
                </div>
            </div>

            <div
                class="flex-shrink-0 p-6 pt-4 border-t border-gray-200 bg-secondary"
            >
                <div class="flex flex-col-reverse sm:flex-row gap-3">
                    <base-button
                        :button-text="$t('allergenFilters.close')"
                        :is-border-transparent="true"
                        class="p-3 px-10 mb-3 sm:block sm:mb-0 md:w-auto"
                        size="sm"
                        variant="secondaryActions"
                        @clicked="closeModal"
                    />

                    <base-button
                        :button-text="$t('allergenFilters.apply')"
                        class="px-12 py-3.5 sm:mb-3 md:mb-0 md:ml-6 md:w-auto"
                        size="sm"
                        @clicked="applyFilters"
                    />
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import { mapMutations } from 'vuex';

import isMobile from '@/helpers/isMobileDevice';
import CloseIcon from '@/components/icons/CloseIcon';
import { UK_PRIMARY_ALLERGENS } from '@/helpers/allergens';

export default {
    name: 'AllergenFiltersModal',
    components: { CloseIcon },
    data() {
        return {
            selectedAllergens: [],
            selectedDietFilters: [],
            showClearedMessage: false
        };
    },
    mounted() {
        this.selectedAllergens = [
            ...this.$store.getters['helpers/allergenFilters']
        ];
        this.selectedDietFilters = [
            ...this.$store.getters['helpers/dietFilters']
        ];
    },
    computed: {
        pivotY() {
            return isMobile() ? 1 : 0.5;
        },
        availableAllergens() {
            const allergens = this.$t('allergens');

            return UK_PRIMARY_ALLERGENS.map(id => ({
                key: id.toString(),
                name: allergens[id]
            }));
        },
        availableDietFilters() {
            return [
                {
                    key: '5',
                    name: this.$t('menuItem.vegetarian')
                },
                {
                    key: '4',
                    name: this.$t('menuItem.vegan')
                },
                {
                    key: '1101',
                    name: this.$t('menuItem.glutenFree')
                },
                {
                    key: '2',
                    name: this.$t('menuItem.halal')
                },
                {
                    key: '3',
                    name: this.$t('menuItem.kosher')
                }
            ];
        }
    },
    methods: {
        ...mapMutations('helpers', ['setAllergenFilters', 'setDietFilters']),
        clearAllFilters() {
            this.selectedAllergens = [];
            this.selectedDietFilters = [];
            this.setAllergenFilters([]);
            this.setDietFilters([]);
            this.showClearedMessage = true;
            setTimeout(() => {
                this.showClearedMessage = false;
            }, 3000);
        },
        applyFilters() {
            this.setAllergenFilters(this.selectedAllergens);
            this.setDietFilters(this.selectedDietFilters);

            this.$modal.hide('allergen-filters-modal');
        },
        closeModal() {
            this.selectedAllergens = [
                ...this.$store.getters['helpers/allergenFilters']
            ];
            this.selectedDietFilters = [
                ...this.$store.getters['helpers/dietFilters']
            ];

            this.$modal.hide('allergen-filters-modal');
        }
    }
};
</script>

<style scoped>
.optin-checkbox {
    color: #172d48;
    color: var(--color-bg-button, #292a31);
}
</style>
